/**
 * The Creator Partner Program's published terms, as constants.
 *
 * A leaf module with NO imports, on purpose. `CREATOR_PROGRAM_TERMS` used to
 * live in `services/creator-rollup.ts`, which reaches the database client and
 * therefore cannot enter a browser bundle — so the web app mirrored the 60-day
 * window by hand (see `apps/web/src/lib/creator-referral.ts`) and the public
 * terms page would have had to retype every other figure. Two hand-maintained
 * copies of a number a contract is written around is a promise waiting to drift
 * from the product. Everything the marketing site and the API both need lives
 * here instead; `creator-rollup.ts` re-exports it so existing callers are
 * unchanged.
 *
 * The web app already imports runtime values from this directory
 * (`schemas/composer-limits`), which is what makes the pattern safe.
 */

/**
 * The published version of the partner terms, stamped onto every application
 * row so we can say which text a partner actually agreed to.
 *
 * An ISO date rather than a counter: the page shows a date, the row stores a
 * date, and nobody has to maintain a mapping between "v3" and what was on the
 * page that week. Bump it whenever the terms text changes materially.
 */
export const CREATOR_PARTNER_TERMS_VERSION = "2026-09-07";

/**
 * The same version, formatted for the "Last updated" line on
 * `/partners/terms`. DERIVED, never a second editable string: the failure mode
 * of two hand-kept dates is a page claiming one date while every consent record
 * claims another, with nothing to say which is the lie.
 */
const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * Same output as `toLocaleDateString("en-GB", { day: "numeric", month: "long",
 * year: "numeric", timeZone: "UTC" })` — e.g. "7 September 2026" — without
 * `Intl`: at module scope that loaded ICU locale data on every API worker cold
 * start, against the 1 s startup limit.
 */
function formatTermsDate(isoDate: string) {
  const [year, month, day] = isoDate.split("-").map(Number);

  return `${day} ${monthNames[(month ?? 1) - 1]} ${year}`;
}

export const CREATOR_PARTNER_TERMS_LAST_UPDATED = formatTermsDate(
  CREATOR_PARTNER_TERMS_VERSION,
);

/**
 * Days a settled order's commission waits before it can be paid out.
 *
 * This is the refund window. Paying a creator on Monday for a subscription
 * that is refunded on Friday would leave money to recover from someone we
 * have no mechanism to recover it from; holding it for a month means the
 * refund lands first and the commission simply never becomes payable.
 */
export const PAYOUT_HOLD_DAYS = 30;

/**
 * The standard offer, in one place.
 *
 * Per-partner terms live on `creator_partners.commission_bps`; these are the
 * program-wide constants that the creator stats page, the public terms page
 * and the terms document all quote, so a change here changes what creators are
 * told in every one of those places at once.
 */
export const CREATOR_PROGRAM_TERMS = {
  /** How long a `/r/` click stays attributable. */
  attributionWindowDays: 60,
  /** The most a negotiated deal goes to; the admin form allows more. */
  maxNegotiatedCommissionBps: 3000,
  minimumPayoutCents: 5000,
  payoutCadence: "monthly",
  payoutHoldDays: PAYOUT_HOLD_DAYS,
  /**
   * Wise was here until the published terms committed us to PayPal and direct
   * bank transfer. `bank_transfer` is already a `creator_payout_method` value
   * and an option in the record-payout dialog, so the ledger has always been
   * able to record one — this is the promise catching up with the product.
   */
  payoutMethods: ["paypal", "bank_transfer"],
  standardCommissionBps: 2000,
} as const;

/**
 * What a referred customer gets off their first qualifying payment. Not a
 * per-partner field: the discount is a property of the Polar code an admin
 * mints (`percentOff`), and this is the figure the marketing pages promise
 * when no campaign says otherwise.
 */
export const STANDARD_CUSTOMER_DISCOUNT_PERCENT = 20;

/** `2000` → `20`. Rates are stored in basis points; humans read percent. */
export function commissionPercent(basisPoints: number) {
  return basisPoints / 100;
}

/** `5000` → `"$50"`. Whole dollars only — every figure here is a round one. */
export function payoutThresholdLabel() {
  return `$${CREATOR_PROGRAM_TERMS.minimumPayoutCents / 100}`;
}
